/* RESPONSIBLE TEAM: team-inbox-experience */
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import type Owner from '@ember/owner';
import storage from 'embercom/vendor/intercom/storage';
import type Session from 'embercom/services/session';
import type TeammateAiTranslationSettings from 'embercom/services/teammate-ai-translation-settings';
import type LanguagesService from 'embercom/services/languages-service';
import type CopilotUsageService from 'embercom/services/copilot-usage-service';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ReactTeammateAppIsland from 'embercom/services/react-teammate-app-island';
import type Store from '@ember-data/store';
import type InboxApi from 'embercom/services/inbox-api';

/**
 * Interface for objects that can be checked for translation permissions.
 * Both Conversation and ConversationSummary satisfy this interface.
 */
export interface TranslationCheckableConversation {
  id: number | string;
  getAttributeForDescriptor?: (id: number | string) => { value?: unknown } | undefined | null;
}

export type TranslateContentReason =
  | 'enabled'
  | 'workspace_disabled'
  | 'teammate_disabled'
  | 'no_copilot_access'
  | 'quota_exceeded'
  | 'manually_disabled'
  | 'excluded_language'
  | 'conversation_not_found';

export interface TranslationResult {
  is_enabled: boolean;
  reason: TranslateContentReason;
}

export interface AccessResult {
  hasAccess: boolean;
  reason: TranslateContentReason;
}

export default class ConversationTranslationSettings extends Service {
  @service declare session: Session;
  @service declare intercomEventService: $TSFixMe;
  @service declare teammateAiTranslationSettings: TeammateAiTranslationSettings;
  @service declare languagesService: LanguagesService;
  @service declare copilotUsageService: CopilotUsageService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIsland;
  @service declare store: Store;

  @tracked private translationDisabledConversations: Set<string> = new Set();
  @tracked private enabledForDefaultLocaleConversations: Set<string> = new Set();
  @tracked private _languageOverride: { conversationId: number; language: string } | undefined;
  @tracked private _additionalLocalesLoaded = false;

  private unsubscribeClearRequest?: () => void;
  private _additionalLocalesLoadStarted = false;

  constructor() {
    super(...arguments);
    this.translationDisabledConversations = new Set(this.disabledConversationIds());
    this.enabledForDefaultLocaleConversations = new Set(
      this.enabledForDefaultLocaleConversationIds(),
    );
    this.unsubscribeClearRequest = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'language-override:clear',
      () => {
        this.clearLanguageOverride();
      },
    );
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeClearRequest?.();
  }

  private loadAdditionalTranslationLocales(): void {
    if (this._additionalLocalesLoadStarted) {
      return;
    }
    this._additionalLocalesLoadStarted = true;
    this.doLoadAdditionalTranslationLocales();
  }

  private async doLoadAdditionalTranslationLocales(): Promise<void> {
    try {
      const settings: $TSFixMe = await this.store.findRecord(
        'general-app-settings',
        this.session.workspace.id,
      );
      if (Array.isArray(settings?.app_locales)) {
        this.session.workspace.additionalTranslationLocales = settings.app_locales;
      }
    } catch (e) {
      console.warn('[ConversationTranslationSettings] failed to load app locales:', e);
    } finally {
      // exit the optimistic loading branch whether the fetch succeeded or failed.
      this._additionalLocalesLoaded = true;
    }
  }

  languageOverrideForConversation(conversationId: number | string): string | undefined {
    let override = this._languageOverride;
    if (override && override.conversationId === Number(conversationId)) {
      return override.language;
    }
    return undefined;
  }

  setLanguageOverride(conversationId: number, language: string): void {
    this._languageOverride = { conversationId, language };
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('language-override:change', {
      conversationId,
      language,
    });
  }

  clearLanguageOverride(): void {
    this._languageOverride = undefined;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('language-override:change', {});
  }

  get autoTranslationEnabled() {
    return (
      this.session.workspace.aiInboxTranslationsEnabled &&
      this.teammateAiTranslationSettings.isInboxTranslationEnabled
    );
  }

  autoTranslationEnabledForConversation(id: string | number | undefined): boolean {
    // NOTE: this id-only variant cannot evaluate the default-locale ad-hoc branch
    // (it has no access to the conversation's detected language). Callers in that
    // flow must use autoTranslationEnabledForConversationObject.
    if (!this.autoTranslationEnabled) {
      return false;
    }

    if (!id) {
      return false;
    }

    return !this.translationDisabledConversations.has(String(id));
  }

  autoTranslationEnabledForConversationObject(conversation: Conversation | undefined): boolean {
    if (!conversation) {
      return false;
    }

    // Check basic workspace and teammate settings
    if (!this.autoTranslationEnabled) {
      return false;
    }

    if (!this.hasTranslationAccessForConversation(conversation)) {
      return false;
    }

    // Excluded-languages gate (unchanged)
    let conversationLanguage = conversation.getAttributeForDescriptor('language')?.value;
    if (
      typeof conversationLanguage === 'string' &&
      this.teammateAiTranslationSettings.excludedLanguages.includes(conversationLanguage)
    ) {
      return false;
    }

    // NEW BRANCH: default-locale + FF + additional locales → consult opt-in set
    if (this.isDefaultLocaleAdHocBranch(conversation)) {
      return this.enabledForDefaultLocaleConversations.has(String(conversation.id));
    }

    // Killswitch engaged on a rolled-out workspace: hard-off ad-hoc translation for
    // default-locale conversations. Without this, the legacy fall-through below
    // would default same-language conversations to enabled, so the killswitch would
    // not actually stop translation (the backend ad-hoc path may still be live).
    if (
      this.session.workspace.isAdHocTranslationsKilled &&
      this.isSameLocaleConversation(conversation)
    ) {
      return false;
    }

    // Existing inverse logic (unchanged behavior, relocated below the new branch)
    if (this.isTranslationManuallyDisabledForConversation(conversation.id)) {
      return false;
    }

    return true;
  }

  getTranslationResultForConversationObject(
    conversation?: TranslationCheckableConversation,
  ): TranslationResult {
    if (!conversation) {
      return { is_enabled: false, reason: 'conversation_not_found' };
    }

    if (!this.session.workspace.aiInboxTranslationsEnabled) {
      return { is_enabled: false, reason: 'workspace_disabled' };
    }

    if (!this.teammateAiTranslationSettings.isInboxTranslationEnabled) {
      return { is_enabled: false, reason: 'teammate_disabled' };
    }

    // Check copilot access
    let accessResult = this.getTranslationAccessForConversation(conversation);
    if (!accessResult.hasAccess) {
      return { is_enabled: false, reason: accessResult.reason };
    }

    // Check excluded languages — must run before the default-locale branch so
    // an excluded source language wins regardless of opt-in state, matching the
    // ordering in `autoTranslationEnabledForConversationObject`.
    let conversationLanguage = conversation.getAttributeForDescriptor?.('language')?.value;
    if (
      typeof conversationLanguage === 'string' &&
      this.teammateAiTranslationSettings.excludedLanguages.includes(conversationLanguage)
    ) {
      return { is_enabled: false, reason: 'excluded_language' };
    }

    // Default-locale ad-hoc branch: callers like `replyToConversation` consult
    // this method to set `replyData.translate_content`. Without this gate a
    // FF-on default-locale conversation that the user has NOT opted in to would
    // still report enabled, causing backend auto-translation despite the UI
    // toggle being OFF.
    if (this.isDefaultLocaleAdHocBranch(conversation)) {
      if (this.enabledForDefaultLocaleConversations.has(String(conversation.id))) {
        return { is_enabled: true, reason: 'enabled' };
      }
      return { is_enabled: false, reason: 'manually_disabled' };
    }

    // Killswitch engaged on a rolled-out workspace: hard-off ad-hoc translation for
    // default-locale conversations so replyData.translate_content is sent false
    // during a kill. The backend ad-hoc path may still be live, so falling through
    // to the legacy enabled-by-default result below would keep translating exactly
    // the conversations the killswitch is meant to stop.
    if (
      this.session.workspace.isAdHocTranslationsKilled &&
      this.isSameLocaleConversation(conversation)
    ) {
      return { is_enabled: false, reason: 'manually_disabled' };
    }

    // Check if manually disabled for this conversation (existing inverse path)
    if (this.isTranslationManuallyDisabledForConversation(conversation.id)) {
      return { is_enabled: false, reason: 'manually_disabled' };
    }

    return { is_enabled: true, reason: 'enabled' };
  }

  @action toggleAutoTranslationEnabledForConversation(id: string | number): void {
    let isEnabled = this.autoTranslationEnabledForConversation(id);
    if (isEnabled) {
      this.addIdToDisabledList(String(id));
      if (this._languageOverride?.conversationId === Number(id)) {
        this.clearLanguageOverride();
      }
    } else {
      this.removeIdFromDisabledList(String(id));
    }
    isEnabled = !isEnabled;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'translation_settings',
      section: 'admin_details',
      context: 'conversation_translation_settings',
      metadata: {
        is_translation_enabled: isEnabled,
        conversation_id: id,
      },
    });
  }

  @action toggleAutoTranslationEnabledForConversationObject(conversation: Conversation): void {
    if (this.isDefaultLocaleAdHocBranch(conversation)) {
      const id = String(conversation.id);
      const wasEnabled = this.enabledForDefaultLocaleConversations.has(id);
      if (wasEnabled) {
        this.removeIdFromEnabledForDefaultLocaleList(id);
        if (this._languageOverride?.conversationId === Number(conversation.id)) {
          this.clearLanguageOverride();
        }
      } else {
        this.addIdToEnabledForDefaultLocaleList(id);
        // The conversation is in the workspace default locale, so the backend's
        // bare `ensure_translations` call (fired on conversation open) does not
        // produce translations for it. Without an explicit target_locale, the
        // UI flips ON but `block.translations[lang]` stays empty and the user
        // sees the original untranslated text. Lazy-look-up inbox-api to avoid
        // a circular @service injection (inbox-api already injects this service).
        const targetLocale = this.session.teammate?.teammatePreferredLocale;
        if (targetLocale) {
          const inboxApi = (getOwner(this) as Owner | undefined)?.lookup('service:inbox-api') as
            | InboxApi
            | undefined;
          inboxApi?.ensureTranslations(conversation, targetLocale).catch(() => {
            // Best-effort: opt-in is already persisted; translations may arrive later
          });
        }
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'translation_settings',
        section: 'admin_details',
        context: 'conversation_translation_settings',
        metadata: {
          is_translation_enabled: !wasEnabled,
          conversation_id: conversation.id,
          branch: 'default_locale_opt_in',
        },
      });
      return;
    }

    // Not in default-locale branch → existing inverse-logic toggle.
    this.toggleAutoTranslationEnabledForConversation(conversation.id);
  }

  isLanguageInExcludedList(language: string) {
    for (let localeId of this.teammateAiTranslationSettings.excludedLanguages) {
      if (this.languagesService.getLanguageName(localeId) === language) {
        return true;
      }
    }
    return false;
  }

  hasTranslationAccessForConversation(conversation?: TranslationCheckableConversation): boolean {
    return this.getTranslationAccessForConversation(conversation).hasAccess;
  }

  // Cheap, synchronous same-language check used by the killswitch path. Unlike the
  // ad-hoc ENABLE branch below it deliberately does NOT require additional locales
  // to be configured or loaded: a kill must hard-off EVERY same-language
  // conversation, and must not depend on the async general-app-settings fetch —
  // which can fail or return empty, exactly the conditions a kill is used in. (That
  // fetch sets _additionalLocalesLoaded = true even on failure, leaving the locale
  // list empty, which would otherwise let the kill be bypassed.)
  //
  // Fail-open is intentional scope, not a leak: when the detected language is absent
  // or non-string (or the workspace locale is unset) this returns false and the kill
  // does NOT fire. Such a conversation is outside the ad-hoc (same-language) scope the
  // killswitch governs — it is ordinary foreign-language translation, which the
  // killswitch must leave alone. The ad-hoc ENABLE branch applies the same guard.
  private isSameLocaleConversation(conversation: TranslationCheckableConversation): boolean {
    const detected = conversation.getAttributeForDescriptor?.('language')?.value;
    return typeof detected === 'string' && detected === this.session.workspace.locale;
  }

  private isDefaultLocaleAdHocBranch(conversation: TranslationCheckableConversation): boolean {
    if (!this.session.workspace.isAdHocTranslationsEnabled) {
      return false;
    }

    // These cheap guards don't depend on the async locale list — check them first
    // so we avoid creating a Glimmer dependency on _additionalLocalesLoaded for
    // conversations that are clearly out of scope for this branch.
    const detected = conversation.getAttributeForDescriptor?.('language')?.value;
    if (typeof detected !== 'string') {
      return false;
    }
    if (detected !== this.session.workspace.locale) {
      return false;
    }

    // Trigger a lazy load so that subsequent renders pick up the real value once the
    // general-app-settings fetch resolves. The guard above means we only reach this
    // code when the killswitch is off AND the rollout flag is on, so the fetch
    // fires at most once per service lifetime.
    this.loadAdditionalTranslationLocales();

    if (!this._additionalLocalesLoaded) {
      return true;
    }

    const additional = this.session.workspace.additionalTranslationLocales;
    return Boolean(additional && additional.length > 0);
  }

  getTranslationAccessForConversation(
    conversation?: TranslationCheckableConversation,
  ): AccessResult {
    // Users with no copilot access are completely blocked
    if (!this.session.teammate.hasCopilotAccess) {
      return { hasAccess: false, reason: 'no_copilot_access' };
    }

    // Check copilot access levels
    let hasUnlimitedAccess = this.session.teammate.hasUnlimitedCopilotAccess;
    if (hasUnlimitedAccess) {
      return { hasAccess: true, reason: 'enabled' };
    }

    // For limited copilot users, check specific scenarios
    if (this.session.teammate.hasLimitedCopilotAccess) {
      // If conversation is already translated, allow access
      if (conversation) {
        let conversationIsAlreadyTranslated =
          conversation.getAttributeForDescriptor?.('auto_translation_applied')?.value === true;
        if (conversationIsAlreadyTranslated) {
          return { hasAccess: true, reason: 'enabled' };
        }
      }

      // For new translations, check if they have quota left.
      // Note: hasTranslationsLeft expects Conversation type but only accesses properties
      // that exist on TranslationCheckableConversation. Consider updating its signature
      // to accept TranslationCheckableConversation in a follow-up PR.
      let hasTranslationsLeft = this.copilotUsageService.hasTranslationsLeft(
        this.session.teammate,
        conversation as Conversation,
      );
      return {
        hasAccess: hasTranslationsLeft,
        reason: hasTranslationsLeft ? 'enabled' : 'quota_exceeded',
      };
    }

    // Fallback - no access
    return { hasAccess: false, reason: 'no_copilot_access' };
  }

  private isTranslationManuallyDisabledForConversation(id: string | number | undefined): boolean {
    if (!id) {
      return false;
    }

    return this.translationDisabledConversations.has(String(id));
  }

  private disabledConversationIds(): string[] {
    let ids = storage.get(this.translationDisabledConversationIdsKey());
    return ids ? ids.split(',') : [];
  }

  private addIdToDisabledList(id: string) {
    let ids = this.disabledConversationIds();
    ids.push(`${id}`);
    storage.set(this.translationDisabledConversationIdsKey(), ids.join(','));

    this.translationDisabledConversations = new Set(this.disabledConversationIds());
    window.dispatchEvent(new CustomEvent('conversation-translation-settings:change'));
  }

  private removeIdFromDisabledList(id: string) {
    let ids = this.disabledConversationIds();
    ids = ids.filter((disabledId) => disabledId !== `${id}`);
    storage.set(this.translationDisabledConversationIdsKey(), ids.join(','));
    this.translationDisabledConversations = new Set(this.disabledConversationIds());
    window.dispatchEvent(new CustomEvent('conversation-translation-settings:change'));
  }

  private translationDisabledConversationIdsKey() {
    let appId = this.session.workspace.id;
    let adminId = this.session.teammate.id;
    return `${appId}::${adminId}::ConversationTranslationSettings::translationDisabled`;
  }

  private enabledForDefaultLocaleConversationIds(): string[] {
    const ids = storage.get(this.enabledForDefaultLocaleConversationIdsKey());
    return ids ? ids.split(',').filter((s: string) => s.length > 0) : [];
  }

  private addIdToEnabledForDefaultLocaleList(id: string) {
    let ids = this.enabledForDefaultLocaleConversationIds();
    // Deduplicate before append (intentional divergence from addIdToDisabledList,
    // which does not dedupe — opt-ins should never accumulate duplicates).
    if (!ids.includes(`${id}`)) {
      ids.push(`${id}`);
    }
    storage.set(this.enabledForDefaultLocaleConversationIdsKey(), ids.join(','));
    this.enabledForDefaultLocaleConversations = new Set(
      this.enabledForDefaultLocaleConversationIds(),
    );
    this.reactTeammateAppIsland.dispatchTeammateAppEvent(
      'conversation-translation-settings:change',
      {},
    );
  }

  private removeIdFromEnabledForDefaultLocaleList(id: string) {
    let ids = this.enabledForDefaultLocaleConversationIds();
    ids = ids.filter((existingId) => existingId !== `${id}`);
    storage.set(this.enabledForDefaultLocaleConversationIdsKey(), ids.join(','));
    this.enabledForDefaultLocaleConversations = new Set(
      this.enabledForDefaultLocaleConversationIds(),
    );
    this.reactTeammateAppIsland.dispatchTeammateAppEvent(
      'conversation-translation-settings:change',
      {},
    );
  }

  private enabledForDefaultLocaleConversationIdsKey() {
    const appId = this.session.workspace.id;
    const adminId = this.session.teammate.id;
    return `${appId}::${adminId}::ConversationTranslationSettings::translationEnabledForDefaultLocale`;
  }
}

declare module '@ember/service' {
  interface Registry {
    conversationTranslationSettings: ConversationTranslationSettings;
    'conversation-translation-settings': ConversationTranslationSettings;
  }
}
